import React from 'react';
import { Link } from 'react-router-dom';
import { heroContent, images, stats, servicesCards, companyInfo, leadership } from '../data/mock';
import { Button } from './ui/button';
import { ArrowRight, CheckCircle, Globe, Shield, Users, Beaker, Phone, Calendar, Award, Heart, MapPin, Sparkles, ZoomIn, X } from 'lucide-react';

const Hero = () => {
  return (
    <section className="relative min-h-[70vh] flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={images.clinical}
          alt="Clinical Research"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0a1628]/85 to-[#0a1628]/70" />
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 bg-[#1877F2]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#87CEEB]/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10 w-full">
        <div className="max-w-3xl space-y-5">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-white/90 text-sm font-medium">Now Enrolling Participants</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Translating Scientific Discovery into Clinical Tools
          </h1>
          
          <p className="text-base md:text-lg text-white/90 leading-relaxed max-w-2xl">
            C Doc Clinical Research Associates is a leading Integrated Research Organization (IRO) committed to delivering innovative and high-quality clinical trials for the pharmaceutical industry and contract research organizations (CROs).
          </p>

          {/* Quick Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-2">
            <Link to="/join-study" data-testid="hero-join-study-btn">
              <Button className="bg-[#1877F2] hover:bg-[#166FE5] text-white px-6 py-4 text-base font-semibold rounded-full shadow-lg shadow-[#1877F2]/25 hover:shadow-xl transition-all duration-300 flex items-center">
                <Calendar className="mr-2 h-5 w-5" />
                Join a Study
              </Button>
            </Link>
            <a href={`tel:${companyInfo.contact.phone}`} data-testid="hero-call-btn">
              <Button className="bg-white text-[#1877F2] hover:bg-[#E6F4FA] px-6 py-4 text-base font-semibold rounded-full shadow-lg transition-all duration-300 flex items-center">
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </Button>
            </a>
            <Link to="/contact" data-testid="hero-contact-btn">
              <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-6 py-4 text-base font-semibold rounded-full transition-all duration-300 backdrop-blur-sm">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// NEW: About Our Research Site Section with Provider Image
const AboutResearchSite = () => {
  const leader = leadership[0];
  
  return (
    <section className="py-16 bg-gradient-to-b from-white to-[#F8FBFF]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center space-x-2 bg-[#1877F2]/10 text-[#1877F2] rounded-full px-4 py-2 mb-4">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-semibold">Our Clinical Research Site</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
            Welcome to <span className="text-[#1877F2]">CDoc Clinical Research Associates</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Provider Image - Properly Fitted */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              {/* Image Container with proper aspect ratio */}
              <div className="aspect-[4/5] w-full max-w-md mx-auto">
                <img
                  src={leader.image}
                  alt={leader.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/80 via-transparent to-transparent" />
              
              {/* Provider Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="inline-block bg-[#1877F2] text-white text-xs font-bold px-3 py-1 rounded-full mb-2">
                  {leader.title}
                </div>
                <h3 className="text-xl font-bold">{leader.name}</h3>
                <div className="flex items-center space-x-2 mt-2 text-white/80 text-sm">
                  <MapPin className="h-4 w-4" />
                  <span>Hyde Park, NY</span>
                </div>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#1877F2]/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#87CEEB]/20 rounded-full blur-2xl" />
          </div>

          {/* AI-Generated Persuasive Biography */}
          <div className="space-y-6">
            <div className="prose prose-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Award className="h-6 w-6 text-[#1877F2] mr-2" />
                Your Trusted Partner in Clinical Excellence
              </h3>
              
              <p className="text-gray-700 leading-relaxed">
                <strong className="text-[#1877F2]">CDoc Clinical Research Associates</strong> stands at the forefront of clinical innovation in the Hudson Valley region. Founded by <strong>Dr. Christina M. Antoine, MD</strong>, a distinguished board-certified internal medicine physician, our research site represents the gold standard in patient-centered clinical trials.
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                With <strong>over 15 years of clinical excellence</strong> and a proven track record of successful trial completions, we have earned the trust of leading pharmaceutical companies and contract research organizations worldwide. Our state-of-the-art facility in Hyde Park, NY, combines cutting-edge research capabilities with the warmth and personalized attention of a community-focused practice.
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                What sets us apart is our <strong>unwavering commitment to participant safety</strong> and our ability to bridge the gap between groundbreaking scientific discovery and real-world patient care. Every clinical trial we conduct is guided by the highest ethical standards, ensuring that participants receive not just exceptional medical oversight, but genuine compassion throughout their research journey.
              </p>
            </div>

            {/* Key Differentiators */}
            <div className="grid grid-cols-2 gap-3 pt-4">
              {[
                { icon: Heart, title: 'Patient-First', desc: 'Compassionate care always', id: 'patient-first' },
                { icon: Award, title: 'Proven Results', desc: '50+ successful trials', id: 'proven-results' },
                { icon: Users, title: 'Expert Team', desc: 'Board-certified physicians', id: 'expert-team' },
                { icon: Globe, title: 'Global Partners', desc: 'Worldwide research network', id: 'global-partners' }
              ].map((item) => (
                <div key={item.id} className="flex items-start space-x-3 p-3 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="w-10 h-10 bg-[#1877F2]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#1877F2]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">{item.title}</h4>
                    <p className="text-gray-600 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3 pt-4">
              <Link to="/providers" data-testid="about-site-meet-team-btn">
                <Button className="bg-[#1877F2] hover:bg-[#166FE5] text-white px-6 py-3 rounded-full font-semibold">
                  Meet Our Team
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/about" data-testid="about-site-learn-more-btn">
                <Button variant="outline" className="border-[#1877F2] text-[#1877F2] px-6 py-3 rounded-full font-semibold">
                  Learn More About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const CompactServices = () => {
  const medicalImages = [
    "https://images.unsplash.com/photo-1576765608689-c0e8f69a46b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxtZWRpY2FsJTIwcmVzZWFyY2glMjBsYWJvcmF0b3J5JTIwY2xpbmljYWx8ZW58MHx8fHwxNzczMjgyNzg2fDA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1758691461957-474a7686e388?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxkb2N0b3IlMjBwYXRpZW50JTIwY2xpbmljYWwlMjBoZWFsdGhjYXJlfGVufDB8fHx8MTc3MzI4Mjc5OXww&ixlib=rb-4.1.0&q=85"
  ];

  return (
    <section className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="text-[#1877F2] font-semibold text-sm uppercase tracking-wider">Our Services</span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">
            Clinical Research <span className="text-[#1877F2]">Services</span>
          </h2>
        </div>

        {/* All Three Images in Parallel - Service Card Centered */}
        <div className="grid md:grid-cols-3 gap-5">
          {/* Left Medical Image */}
          <div className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <img
              src={medicalImages[0]}
              alt="Medical research laboratory"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              style={{ minHeight: '280px' }}
            />
          </div>

          {/* Center - Service Card */}
          {servicesCards.map((card) => (
            <div
              key={card.id}
              className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
              data-testid={`service-card-${card.id}`}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <h3 className="absolute bottom-3 left-3 right-3 text-white font-bold text-base">
                  {card.title}
                </h3>
              </div>
              <div className="p-4">
                <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 mb-3">
                  {card.description}
                </p>
                <Link 
                  to={card.link}
                  className="inline-flex items-center text-[#1877F2] font-semibold text-sm hover:text-[#166FE5] transition-colors"
                >
                  {card.linkText}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}

          {/* Right Medical Image */}
          <div className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <img
              src={medicalImages[1]}
              alt="Doctor consulting patient"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              style={{ minHeight: '280px' }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const CompactCTA = () => {
  return (
    <section className="py-12 bg-gradient-to-r from-[#1877F2] to-[#0a1628] relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-[#87CEEB]/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
          Ready to Participate in Clinical Research?
        </h2>
        <p className="text-white/80 text-base mb-6 max-w-2xl mx-auto">
          Join our clinical trials and contribute to groundbreaking medical advancements.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/join-study" data-testid="cta-join-study-btn">
            <Button className="bg-white text-[#1877F2] hover:bg-[#E6F4FA] px-8 py-4 text-base font-bold rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center">
              <Calendar className="mr-2 h-5 w-5" />
              Join a Study
            </Button>
          </Link>
          <a href={`tel:${companyInfo.contact.phone}`} data-testid="cta-call-btn">
            <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 text-base font-bold rounded-full transition-all duration-300 flex items-center justify-center">
              <Phone className="mr-2 h-5 w-5" />
              {companyInfo.contact.phone}
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Clinical Trials Info Section with Images - Clickable to Enlarge
const ClinicalTrialsInfo = () => {
  const [selectedImage, setSelectedImage] = React.useState(null);
  
  const clinicalImages = [
    {
      src: "https://customer-assets.emergentagent.com/job_hyde-park-clinic/artifacts/z3ptm08f_clinical%20t.jpeg",
      title: "What is a Clinical Trial?",
      description: "Clinical trials are research studies that test new treatments, interventions, or tests to prevent, detect, or treat diseases."
    },
    {
      src: "https://customer-assets.emergentagent.com/job_hyde-park-clinic/artifacts/c4gp6ccc_stages-of-clinical-research.webp",
      title: "Stages of Research",
      description: "Clinical research progresses through phases: Discovery, Preclinical, Clinical Trials (Phase I-IV), and FDA Review."
    },
    {
      src: "https://customer-assets.emergentagent.com/job_hyde-park-clinic/artifacts/xllks4yc_shutterstock_606100106-2.jpg",
      title: "Why Participate?",
      description: "Participants help advance medical knowledge while potentially gaining access to new treatments before they're widely available."
    }
  ];

  return (
    <>
      <section className="py-10 bg-[#F8FBFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-[#1877F2] font-semibold text-sm uppercase tracking-wider">Understanding Clinical Trials</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">
              The Journey of <span className="text-[#1877F2]">Medical Research</span>
            </h2>
            <p className="text-gray-500 text-xs mt-2">Click any image to enlarge</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {clinicalImages.map((item) => (
              <div 
                key={item.title} 
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group"
                onClick={() => setSelectedImage(item)}
                data-testid={`clinical-image-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="relative">
                  <img 
                    src={item.src} 
                    alt={item.title}
                    className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/30">
                    <div className="bg-[#1877F2]/80 text-white p-2 rounded-full">
                      <ZoomIn className="w-5 h-5" />
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 text-sm mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-xs leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Modal/Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="clinical-image-modal"
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img 
              src={selectedImage.src} 
              alt={selectedImage.title}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4">
              <h3 className="text-white text-xl font-bold">{selectedImage.title}</h3>
              <p className="text-white/80 text-sm mt-2">{selectedImage.description}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const HeroSection = () => {
  return (
    <>
      <Hero />
      <CompactServices />
      <ClinicalTrialsInfo />
      <CompactCTA />
    </>
  );
};

export default HeroSection;
